/**
 * The current user's Hydden-staff standing, as reported by control-api.
 *
 * Mesh's own `AuthContext` cannot answer this: it holds only
 * `{email, name, userID}` and nothing in the Mesh UI fetches roles. Staff tier
 * is a fact about the Control principal, so it comes from control-api.
 */
export interface StaffIdentity {
  userId: string;
  email: string;
  name: string;
  /** Always a string; `''` when the Control session carries no tenant. */
  tenantId: string;
  isSuperuser: boolean;
  isTenantCreator: boolean;
}

/**
 * What the admin area lets a user see.
 *
 * These are a ladder, not a set of disjoint labels: `superuser` reaches
 * everything a `tenant_creator` can, plus the six migrated admin pages. Use
 * {@link satisfiesTier} rather than comparing tiers with `===`, or a superuser
 * is locked out of anything marked `tenant_creator` — which is the wrong way
 * round, and where a plain equality check lands.
 */
export type StaffTier = 'superuser' | 'tenant_creator' | 'none';

/** Higher outranks lower. Kept next to the type so the two cannot drift. */
const TIER_RANK: Record<StaffTier, number> = {
  none: 0,
  tenant_creator: 1,
  superuser: 2,
};

/**
 * Whether `held` meets a `required` tier. `required` names a minimum, so a
 * superuser satisfies `tenant_creator`.
 */
export function satisfiesTier(held: StaffTier, required: StaffTier): boolean {
  return TIER_RANK[held] >= TIER_RANK[required];
}

export function staffTierOf(identity: StaffIdentity | null): StaffTier {
  if (identity?.isSuperuser) {
    return 'superuser';
  }
  if (identity?.isTenantCreator) {
    return 'tenant_creator';
  }
  return 'none';
}

/** Whether the user may reach the admin area at all, in any capacity. */
export function isStaff(identity: StaffIdentity | null): boolean {
  return staffTierOf(identity) !== 'none';
}

/** The two staff groups control-api stores (HYD-2112). */
export type StaffGroup = 'superuser' | 'tenant_creator';

/**
 * One row of `GET /api/admin/staff`. Emails arrive lowercased and `grantedAt`
 * is RFC 3339 UTC. A superuser appears only under `superuser`: the tier is
 * implicitly `tenant_creator` too, but the server does not repeat it there.
 */
export interface StaffMember {
  email: string;
  group: StaffGroup;
  grantedBy: string;
  grantedAt: string;
}

/** Both groups, always present. An empty `tenantCreator` is the normal first state. */
export interface StaffMembers {
  superuser: StaffMember[];
  tenantCreator: StaffMember[];
}
